// src/hooks/useInternalChat.ts
// Firestore direct — sans NestJS
import { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth, functions } from '@/lib/firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, updateDoc, doc, getDocs, getDoc, Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type {
  Conversation, Message, CreateConversationRequest, SendMessageRequest,
} from '@/types/internal-communication.types';

const ADMIN_UID = '3p1QI94OjBMLtMSscUmccy05BZr1';
const LEGACY_IDS = ['acquizgroup-admin', 'acquizgroup-support', 'deal-coordinator'];

export function useInternalChat(dealId?: string) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unsubMessages = useRef<(() => void) | null>(null);

  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) { setLoadingConversations(false); return; }

    const isAdmin = currentUser.uid === ADMIN_UID;
    const mergedMap = new Map<string, Conversation>();
    const unsubList: (() => void)[] = [];

    const handleSnap = (snap: any) => {
      snap.docs.forEach((d: any) => {
        mergedMap.set(d.id, {
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
          updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
          lastMessageAt: d.data().lastMessageAt?.toDate?.() || null,
        } as Conversation);
      });
      const sorted = Array.from(mergedMap.values()).sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setConversations(sorted);
      setLoadingConversations(false);
    };

    const makeQuery = (id: string) => dealId
      ? query(collection(db, 'conversations'), where('participantIds', 'array-contains', id), where('dealId', '==', dealId), orderBy('updatedAt', 'desc'))
      : query(collection(db, 'conversations'), where('participantIds', 'array-contains', id), orderBy('updatedAt', 'desc'));

    const ids = [currentUser.uid];
    if (isAdmin) LEGACY_IDS.forEach(id => ids.push(id));

    ids.forEach(id => {
      const unsub = onSnapshot(makeQuery(id), handleSnap, (err) => {
        console.error('[useInternalChat] error:', err);
        setError(err.message);
        setLoadingConversations(false);
      });
      unsubList.push(unsub);
    });

    return () => unsubList.forEach(u => u());
  }, [currentUser?.uid, dealId]);

  const selectConversation = useCallback((conv: Conversation | null) => {
    setSelectedConversation(conv);
    setMessages([]);

    if (unsubMessages.current) {
      unsubMessages.current();
      unsubMessages.current = null;
    }

    if (!conv) return;
    setLoadingMessages(true);

    const q = query(
      collection(db, 'conversations', conv.id, 'messages'),
      orderBy('createdAt', 'asc')
    );

    unsubMessages.current = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || new Date(),
      })) as Message[];
      setMessages(msgs);
      setLoadingMessages(false);
    }, (err) => {
      console.error('[useInternalChat] messages error:', err);
      setLoadingMessages(false);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (unsubMessages.current) unsubMessages.current();
    };
  }, []);

  const createConversation = useCallback(async (req: CreateConversationRequest): Promise<Conversation> => {
    if (!currentUser) throw new Error('Not authenticated');

    const participantIds = Array.from(new Set([currentUser.uid, ...req.participantIds]));

    if (req.type === 'private' && participantIds.length === 2) {
      const snap = await getDocs(
        query(
          collection(db, 'conversations'),
          where('participantIds', 'array-contains', currentUser.uid)
        )
      );
      const existing = snap.docs.find(d => {
        const ids = d.data().participantIds as string[];
        return (
          ids.length === 2 &&
          ids.includes(participantIds[0]) &&
          ids.includes(participantIds[1]) &&
          !d.data().dealId
        );
      });
      if (existing) {
        const conv = {
          id: existing.id,
          ...existing.data(),
          createdAt: existing.data().createdAt?.toDate?.() || new Date(),
          updatedAt: existing.data().updatedAt?.toDate?.() || new Date(),
        } as Conversation;
        selectConversation(conv);
        return conv;
      }
    }

    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, 'conversations'), {
      dealId: req.dealId || null,
      type: req.type || 'private',
      participantIds,
      participants: participantIds.map(id => ({
        userId: id,
        userName: id === currentUser.uid
          ? (currentUser.displayName ||
            (currentUser.uid === ADMIN_UID ? 'Équipe AcquizGroup' : 'Staff AcquizGroup'))
          : 'Contact',
        userRole: 'member',
        joinedAt: now,
        lastReadAt: null,
        unreadCount: 0,
      })),
      title: req.title || null,
      lastMessage: req.initialMessage || null,
      lastMessageAt: now,
      lastMessageSenderId: req.initialMessage ? currentUser.uid : null,
      isArchived: false,
      createdAt: now,
      updatedAt: now,
    });

    if (req.initialMessage) {
      await addDoc(collection(db, 'conversations', docRef.id, 'messages'), {
        conversationId: docRef.id,
        fromUserId: currentUser.uid,
        fromUserName: currentUser.displayName ||
          (currentUser.uid === ADMIN_UID ? 'Équipe AcquizGroup' : 'Staff AcquizGroup'),
        fromUserRole: 'member',
        content: req.initialMessage,
        attachments: [],
        isInternal: false,
        readBy: [currentUser.uid],
        createdAt: now,
      });
    }

    const newConv: Conversation = {
      id: docRef.id,
      dealId: req.dealId,
      type: req.type || 'private',
      participantIds,
      participants: [],
      title: req.title,
      lastMessage: req.initialMessage,
      lastMessageAt: new Date(),
      isArchived: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    selectConversation(newConv);
    return newConv;
  }, [currentUser?.uid, selectConversation]);

  const sendMessage = useCallback(async (req: SendMessageRequest): Promise<void> => {
    if (!currentUser || !selectedConversation) return;
    if (!req.content?.trim()) return;

    setSending(true);
    try {
      const now = Timestamp.now();
      const convId = req.conversationId || selectedConversation.id;
      const originalContent = req.content.trim();

      // ── Traduction automatique ────────────────────────────────
const liveUser = auth.currentUser; // lire en temps réel
const senderIsStaff = liveUser?.uid === ADMIN_UID ||
  LEGACY_IDS.includes(liveUser?.uid || '');
const sourceLang = senderIsStaff ? 'fr' : 'en';

      const recipientIds = (selectedConversation.participantIds || []).filter(
        (id: string) => id !== currentUser.uid && !LEGACY_IDS.includes(id)
      );

      let translatedContent = originalContent;

      if (recipientIds.length > 0) {
        try {
          const recipientDoc = await getDoc(doc(db, 'userProfiles', recipientIds[0]));
          let targetLang = 'en';
          if (recipientDoc.exists()) {
            const profile = recipientDoc.data();
            targetLang = profile.preferredLanguage || profile.language || 'en';
          }

          if (targetLang !== sourceLang) {
            const translateFn = httpsCallable(functions, 'translateText');
            const result = await translateFn({
              text: originalContent,
              sourceLang,
              targetLang,
            }) as any;
            translatedContent = result.data?.translation || originalContent;
          }
        } catch (translateErr) {
          console.warn('[useInternalChat] Translation failed, using original:', translateErr);
          translatedContent = originalContent;
        }
      }
      // ── Fin traduction ────────────────────────────────────

      await addDoc(collection(db, 'conversations', convId, 'messages'), {
        conversationId: convId,
        fromUserId: currentUser.uid,
        fromUserName: currentUser.displayName ||
          (currentUser.uid === ADMIN_UID ? 'Équipe AcquizGroup' : 'Staff AcquizGroup'),
        fromUserRole: 'member',
        content: translatedContent,
        originalContent: translatedContent !== originalContent ? originalContent : null,
        attachments: [],
        isInternal: req.isInternal || false,
        replyToId: req.replyToId || null,
        readBy: [currentUser.uid],
        createdAt: now,
      });

      await updateDoc(doc(db, 'conversations', convId), {
        lastMessage: translatedContent,
        lastMessageAt: now,
        lastMessageSenderId: currentUser.uid,
        updatedAt: now,
      });

    } catch (err: any) {
      console.error('[useInternalChat] sendMessage error:', err);
      setError(err.message);
      throw err;
    } finally {
      setSending(false);
    }
  }, [currentUser?.uid, selectedConversation]);

  const archiveConversation = useCallback(async (convId: string): Promise<void> => {
    await updateDoc(doc(db, 'conversations', convId), {
      isArchived: true,
      updatedAt: Timestamp.now(),
    });
  }, []);

  const unreadCount = conversations.reduce((acc, conv) => {
    const participant = conv.participants?.find(p => p.userId === currentUser?.uid);
    return acc + (participant?.unreadCount || 0);
  }, 0);

  return {
    conversations,
    selectedConversation,
    messages,
    loadingConversations,
    loadingMessages,
    sending,
    error,
    unreadCount,
    selectConversation,
    createConversation,
    sendMessage,
    archiveConversation,
  };
}